/**
 * Una fecha como llega de Firestore. Gemelo de `FirestoreDateLike` de
 * `@api/Orders/OrderPayments` (que no se importa aquí: ése es un módulo de
 * PARIDAD admin↔web y esto es un util local).
 */
export type FirestoreDateLike =
    | Date
    | string
    | number
    | { seconds?: number; nanoseconds?: number; toDate?: () => Date; toMillis?: () => number }
    | null
    | undefined;

/**
 * Una fecha de Firestore → `Date`, tolerando las CUATRO formas en que llega, o
 * `undefined` si no se puede: **nunca un `Invalid Date`**, que es lo que revienta
 * río abajo (`format()` de date-fns lanza `RangeError`).
 *
 * ## Es LA implementación. No se escribe otra.
 *
 * Hasta el 2026-09-11 había **doce** copias de esto en el admin, porque la
 * primera (`parseFirebaseDate`) estaba rota —preguntaba `date.seconds &&
 * date.nanoseconds`, y **`nanoseconds: 0` es falsy**, así que un Timestamp
 * redondo al segundo se caía al `new Date(objeto)` y salía `Invalid Date`— y cada
 * módulo que se topó con el bug lo esquivó escribiendo su propia versión en vez
 * de arreglarlo. Cada copia esquivaba el bug original y traía el suyo:
 *
 * - las que devolvían `0` millis para un Timestamp crudo (`OrdersBoardView`)
 *   mandaban el pedido al extremo del orden;
 * - las que devolvían `undefined` (`@utils/OrdersBoard`) apagaban la alarma de
 *   pedido retrasado;
 * - las que hacían `new Date(objeto)` (`CashRegisterServ`) daban `NaN`, y como
 *   toda comparación con `NaN` es `false`, una caja vieja dejaba de bloquear y
 *   una caja de hoy dejaba de adoptarse **sin un solo error en consola**;
 * - las que exigían `instanceof Timestamp` (`ModalViewOrder`) pintaban "Fecha
 *   inválida" para cualquier fecha que no viniera del SDK.
 *
 * Todas delegan aquí desde el 2026-09-11. Si necesitas una fecha de Firestore,
 * **importa esto**; si te hace falta algo que no cubre, se agrega aquí con su
 * prueba.
 */
export const toDateSafe = (value: FirestoreDateLike): Date | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
    // Timestamp de Firestore (el SDK devuelve objetos con `toDate`/`toMillis`)
    if (typeof value === 'object' && typeof value.toMillis === 'function') {
        const millis = value.toMillis();
        return Number.isFinite(millis) ? new Date(millis) : undefined;
    }
    if (typeof value === 'object' && typeof value.toDate === 'function') {
        const fromTimestamp = value.toDate();
        return fromTimestamp instanceof Date && !Number.isNaN(fromTimestamp.getTime()) ? fromTimestamp : undefined;
    }
    // Timestamp CRUDO: `{ seconds, nanoseconds }`, como llega de un export, de un
    // doc escrito por el server o de un objeto que pasó por JSON (el espejo del
    // POS, Redux persistido). `nanoseconds` puede ser 0 — y 0 es un valor.
    if (typeof value === 'object' && typeof value.seconds === 'number') {
        const millis = value.seconds * 1000 + (Number(value.nanoseconds) || 0) / 1000000;
        return Number.isFinite(millis) ? new Date(millis) : undefined;
    }
    const parsed = new Date(value as string | number);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

/**
 * Lo mismo en milisegundos, y **`0` cuando no hay fecha utilizable**.
 *
 * El `0` es a propósito y es lo que ya esperaban los comparadores que existían
 * (`sort`, ventanas de caja): ordena al principio en vez de envenenar la
 * comparación con `NaN`. ⚠️ Para PREGUNTAR por una fecha (¿es de hoy?, ¿es
 * vieja?) no sirve un `0` disfrazado de fecha: ahí se usa `toDateSafe` y se
 * decide explícitamente qué hacer con `undefined`.
 */
export const toMillisSafe = (value: FirestoreDateLike): number => toDateSafe(value)?.getTime() ?? 0;
